
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { Home } from 'lucide-react';

const BreadcrumbNav: React.FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(segment => segment);
  
  // Skip rendering breadcrumbs on home page
  if (pathSegments.length === 0) return null;
  
  // Create readable names for path segments
  const getReadableName = (segment: string, index: number): string => {
    // Handle IDs (typically when they're UUIDs or numeric)
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(segment) || 
        /^\d+$/.test(segment)) {
      // For pet IDs, we return "Details" as it's more user-friendly
      return "Détails";
    }
    
    // Handle common paths
    switch (segment) {
      case 'pets': return 'Animaux';
      case 'add': return 'Ajouter';
      case 'add-pet': return 'Ajouter un animal';
      case 'health': return 'Santé';
      case 'calendar': return 'Calendrier';
      case 'breeders': return 'Éleveurs';
      case 'breeder-dashboard': return 'Tableau de bord éleveur';
      case 'shop': return 'Boutique';
      case 'profile': return 'Profil';
      case 'list': return 'Liste';
      case 'vets': return 'Vétérinaires';
      case 'forum': return 'Forum';
      case 'gallery': return 'Galerie';
      case 'settings': return 'Paramètres';
      case 'lost-pets': return 'Animaux perdus';
      case 'medications': return 'Médicaments';
      case 'activities': return 'Activités';
      case 'activity': return 'Activité';
      case 'food': return 'Alimentation';
      case 'vaccines': return 'Vaccins';
      case 'deworming': return 'Vermifuges';
      case 'hygiene': return 'Hygiène';
      case 'weight': return 'Poids';
      case 'competitions': return 'Compétitions';
      case 'edit': return 'Modifier';
      case 'dashboard': return 'Tableau de bord';
      case 'welcome': return 'Bienvenue';
      case 'subscription': return 'Abonnement';
      default: return segment.charAt(0).toUpperCase() + segment.slice(1);
    }
  };

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">
              <Home className="h-4 w-4" />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        
        {pathSegments.map((segment, index) => {
          const isLast = index === pathSegments.length - 1;
          const path = `/${pathSegments.slice(0, index + 1).join('/')}`;
          
          return (
            <div key={path} className="inline-flex items-center gap-1.5">
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{getReadableName(segment, index)}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={path}>{getReadableName(segment, index)}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </div>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default BreadcrumbNav;
